/**
 * Presentation-only copy for the SVR detail page.
 * Status transitions, permissions, and notifications stay on the server.
 */

export const SVR_FOCUSED_STAGES = [
  {
    id: "service",
    label: "Service",
    description: "Visit, diagnose and complete the service report",
  },
  {
    id: "pic",
    label: "PIC review",
    description: "Review customer report and billing decision",
  },
  {
    id: "finance",
    label: "Finance",
    description: "Invoice, payment or approved waiver",
  },
  {
    id: "close",
    label: "Close",
    description: "Customer communication and completion",
  },
] as const;

export type SvrFocusedStageId = (typeof SVR_FOCUSED_STAGES)[number]["id"];

export type SvrFocusedPrimaryKind =
  | "focus-notes"
  | "submit-update"
  | "focus-pic"
  | "focus-finance"
  | "focus-close"
  | "none";

export type SvrFocusedWorkflowInput = {
  status: string | null | undefined;
  technicianAssigned: boolean;
  cleoFieldSessionActive: boolean;
  arrivedAt?: string | Date | null;
  departedAt?: string | Date | null;
  hasWorkNotes: boolean;
  hasCustomerReport: boolean;
  updateSubmitted: boolean;
  reportSent: boolean;
  picAssigned: boolean;
  customerUnmatched: boolean;
  scheduleReady: boolean;
  billable: boolean;
  invoicePaid: boolean;
};

export type SvrFocusedWorkflowView = {
  stageId: SvrFocusedStageId;
  stageLabel: string;
  headerBadge: string;
  /** Operational stage only. Never a customer/address confirmation. */
  currentStage: string;
  completed: string[];
  nextTitle: string;
  nextDetail: string;
  primaryLabel: string | null;
  primaryKind: SvrFocusedPrimaryKind;
  blocker: string | null;
};

const STATUS_BADGE: Record<string, string> = {
  scheduled: "Scheduled",
  pending_review: "Pending review",
  report_sent: "Report sent",
  awaiting_payment: "Awaiting payment",
  closed: "Closed",
  waived: "Waived",
  cancelled: "Cancelled",
};

function statusKey(status: string | null | undefined): string {
  return String(status ?? "").trim().toLowerCase();
}

export function svrFocusedStageId(status: string | null | undefined): SvrFocusedStageId {
  switch (statusKey(status)) {
    case "pending_review":
      return "pic";
    case "report_sent":
    case "awaiting_payment":
      return "finance";
    case "closed":
    case "waived":
    case "cancelled":
      return "close";
    default:
      return "service";
  }
}

export function canViewSvrFinancialDetails(role: string | null | undefined): boolean {
  const key = String(role ?? "").trim().toLowerCase();
  return key === "admin" || key === "finance" || key === "sales";
}

function hasInstant(value: string | Date | null | undefined): boolean {
  if (value == null) return false;
  if (value instanceof Date) return !Number.isNaN(value.getTime());
  return String(value).trim().length > 0;
}

export function buildSvrFocusedWorkflow(input: SvrFocusedWorkflowInput): SvrFocusedWorkflowView {
  const status = statusKey(input.status);
  const stageId = svrFocusedStageId(status);
  const stage = SVR_FOCUSED_STAGES.find((item) => item.id === stageId) ?? SVR_FOCUSED_STAGES[0];
  const badge = STATUS_BADGE[status] ?? "Scheduled";
  const completed: string[] = [];
  if (input.technicianAssigned) completed.push("Technician assigned");
  if (input.updateSubmitted) completed.push("Site update submitted");
  if (input.reportSent) completed.push("Report sent to customer");
  if (input.invoicePaid) completed.push("Payment recorded");
  if (status === "closed") completed.push("Visit closed");
  if (status === "waived") completed.push("Visit waived");

  let currentStage = "Not started";
  let nextTitle = "Begin the site visit";
  let nextDetail =
    "Confirm arrival, then document the work and outcome before submitting for PIC review.";
  let primaryLabel: string | null = "START SITE VISIT";
  let primaryKind: SvrFocusedPrimaryKind = "focus-notes";
  let blocker: string | null = null;

  if (input.customerUnmatched) {
    blocker = "This visit is not linked to a customer yet.";
  } else if (status === "scheduled" && !input.scheduleReady && !input.cleoFieldSessionActive) {
    blocker = "Set the arrival time before starting the visit.";
  }

  if (status === "cancelled") {
    currentStage = "Cancelled";
    nextTitle = "This visit was cancelled";
    nextDetail = "No further workflow action is available on a cancelled visit.";
    primaryLabel = null;
    primaryKind = "none";
  } else if (status === "closed") {
    currentStage = "Completed";
    nextTitle = "Visit completed";
    nextDetail = "Service, review, and closure are finished.";
    primaryLabel = null;
    primaryKind = "none";
  } else if (status === "waived") {
    currentStage = "Completed";
    nextTitle = "Visit waived";
    nextDetail = "This visit is closed with an approved waiver.";
    primaryLabel = null;
    primaryKind = "none";
  } else if (status === "scheduled") {
    const onSite =
      input.cleoFieldSessionActive ||
      (hasInstant(input.arrivedAt) && !hasInstant(input.departedAt));
    if (onSite) {
      currentStage = "Visit in progress";
      nextTitle = "Visit in progress";
      nextDetail = "Document the work, then submit the update for PIC review when the field session ends.";
      primaryLabel = null;
      primaryKind = "none";
      blocker = input.cleoFieldSessionActive
        ? "Field session in progress. Save and proceed is available when the session ends."
        : blocker;
    } else if (hasInstant(input.departedAt) && !input.updateSubmitted) {
      currentStage = "Report required";
      nextTitle = "Record the service report";
      nextDetail = "Add the work performed and findings, then submit the update for PIC review.";
      primaryLabel = input.hasWorkNotes ? "SAVE AND PROCEED" : "START SITE VISIT";
      primaryKind = input.hasWorkNotes ? "submit-update" : "focus-notes";
    } else if (input.technicianAssigned) {
      currentStage = "Assigned technician";
      nextTitle = "Begin the site visit";
      nextDetail =
        "Confirm arrival, then document the work and outcome before submitting for PIC review.";
      primaryLabel = input.hasWorkNotes ? "SAVE AND PROCEED" : "START SITE VISIT";
      primaryKind = input.hasWorkNotes ? "submit-update" : "focus-notes";
    } else {
      currentStage = "Not started";
      primaryLabel = input.hasWorkNotes ? "SAVE AND PROCEED" : "START SITE VISIT";
      primaryKind = input.hasWorkNotes ? "submit-update" : "focus-notes";
    }
  } else if (status === "pending_review") {
    currentStage = input.hasCustomerReport ? "Awaiting customer confirmation" : "Report required";
    nextTitle = input.hasCustomerReport ? "Send the customer report" : "Write the customer report";
    nextDetail = input.hasCustomerReport
      ? "Review the customer-facing report and billing decision, then send or close."
      : "The site update is in. A customer-facing report is still required before PIC can close or send.";
    primaryLabel = "REVIEW REPORT";
    primaryKind = "focus-pic";
    if (!input.picAssigned) {
      blocker = "No PIC is assigned yet.";
    }
  } else if (status === "report_sent" || status === "awaiting_payment") {
    if (input.billable && !input.invoicePaid) {
      currentStage = status === "awaiting_payment" ? "Awaiting payment" : "Awaiting payment";
      nextTitle = "Finance follow-up";
      nextDetail = "Invoice, payment, or an approved waiver is the remaining step before closure.";
    } else if (!input.reportSent) {
      currentStage = "Awaiting customer confirmation";
      nextTitle = "Confirm the customer report";
      nextDetail = "The report still needs to be sent before this visit can close.";
    } else {
      currentStage = "Awaiting customer confirmation";
      nextTitle = "Finish customer communication";
      nextDetail = "Confirm the customer has the report, then complete closure.";
    }
    primaryLabel = "OPEN FINANCE";
    primaryKind = "focus-finance";
  }

  if (input.cleoFieldSessionActive && primaryKind === "submit-update") {
    primaryLabel = null;
    primaryKind = "none";
    blocker = "Field session in progress. Save and proceed is available when the session ends.";
  }

  return {
    stageId,
    stageLabel: stage.label,
    headerBadge: status === "cancelled" ? "Cancelled" : `${badge} · ${stage.label}`,
    currentStage,
    completed,
    nextTitle,
    nextDetail,
    primaryLabel,
    primaryKind,
    blocker,
  };
}

export type SvrActivityEvent = {
  id: string;
  action: string;
  person: string | null;
  at: string | Date;
};

export type SvrActivitySource = {
  createdAt?: string | Date | null;
  createdBy?: string | null;
  custWhatsappSentAt?: string | Date | null;
  custWhatsappSentBy?: string | null;
  arrivedAt?: string | Date | null;
  departedAt?: string | Date | null;
  updateSubmittedAt?: string | Date | null;
  updateSubmittedBy?: string | null;
  reportSentAt?: string | Date | null;
  reportSentBy?: string | null;
  closedAt?: string | Date | null;
  closedBy?: string | null;
  /** Set when the visit was auto-closed on technician done (billing pending). */
  autoClosedAt?: string | Date | null;
  cancelledAt?: string | Date | null;
  cancelledByName?: string | null;
  waiverRequestedAt?: string | Date | null;
  waiverRequestedBy?: string | null;
  paidAt?: string | Date | null;
  paidBy?: string | null;
};

function pushEvent(
  events: SvrActivityEvent[],
  id: string,
  action: string,
  at: string | Date | null | undefined,
  person?: string | null,
) {
  if (!hasInstant(at) || at == null) return;
  const name = String(person ?? "").trim();
  events.push({ id, action, person: name.length > 0 ? name : null, at });
}

/** Timeline built only from timestamps already stored on the visit. */
export function buildSvrActivityEvents(
  source: SvrActivitySource,
  options: { includeFinancial: boolean },
): SvrActivityEvent[] {
  const events: SvrActivityEvent[] = [];
  pushEvent(events, "created", "Visit created", source.createdAt, source.createdBy);
  pushEvent(
    events,
    "whatsapp",
    "Customer WhatsApp notification sent",
    source.custWhatsappSentAt,
    source.custWhatsappSentBy,
  );
  pushEvent(events, "arrived", "Arrival recorded", source.arrivedAt, null);
  pushEvent(events, "departed", "Departure recorded", source.departedAt, null);
  pushEvent(events, "update", "Site update submitted", source.updateSubmittedAt, source.updateSubmittedBy);
  pushEvent(events, "report", "Report sent to customer", source.reportSentAt, source.reportSentBy);
  if (options.includeFinancial) {
    pushEvent(events, "waiver", "Waiver requested", source.waiverRequestedAt, source.waiverRequestedBy);
    pushEvent(events, "paid", "Payment recorded", source.paidAt, source.paidBy);
  }
  pushEvent(
    events,
    "closed",
    source.autoClosedAt ? "Visit auto-closed (billing pending)" : "Visit closed",
    source.closedAt,
    source.closedBy,
  );
  pushEvent(events, "cancelled", "Visit cancelled", source.cancelledAt, source.cancelledByName);
  events.sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime());
  return events;
}
